import React, { useEffect, useMemo, useState } from "react";
import styles from "./style.module.scss";
import { post } from "utils/ApiUtil";
import { FontText } from "components/FontText";
import { FileUploadButton } from "components/FileUploadButton";
import { Input } from "components/Input";
import { IFiles } from "data/common";
import { headerState } from "recoil/header";
import { useSetRecoilState } from "recoil";
import { SvgCheckLine, Svgs, SvgX } from "assets";
import Modal from "components/Modal";
import useNav from "hooks/useNav";
import { ScreenId } from "utils";
import { Select } from "components";
import { CONSTANTS } from "assets/constant";
import useUser from "hooks/useUser";

interface INiceapiMain {
  req_no: string;
  req_dtim: string;
  resultVal: string;
  key: string;
  iv: string;
  hmac_key: string;
  plain: string;
  enc_data: string;
  integrity: string;
  token_version_id: string;
  token_val: string;
}

interface INiceapiResult {
  authtype: string;
  nationalinfo: string;
  responseno: string;
  resultcode: string;
  enctime: string;
  requestno: string;
  mobileco: string;
  mobileno: string;
  sitecode: string;
  di: string;
  ci: string;
  receivedata: string;
  birthdate: string;
  gender: string;
  name: string;
}

type TType = "본인" | "가족";
interface IInputData {
  company_name?: string;
  type: TType;
  files?: IFiles[];
  files2?: IFiles[];
}

export default function Page() {
  const { navigate } = useNav();
  const { user } = useUser();
  const setHeader = useSetRecoilState(headerState);
  const [step, setStep] = useState(0);
  const [passData, setPassData] = useState<INiceapiResult>();
  const [inputData, setInputData] = useState<IInputData>({ type: "본인" });
  const [isAgreement, setIsAgreement] = useState(false);
  const [isAgreementModal, setIsAgreementModal] = useState(false);

  const isNext: boolean = useMemo(() => {
    if (
      !inputData.company_name ||
      (inputData.files || []).length !== 1 ||
      !inputData.type
    ) {
      return false;
    } else if (
      inputData.type === "가족" &&
      (inputData.files2 || []).length === 0
    ) {
      return false;
    }
    if (!isAgreement) {
      return false;
    }
    return true;
  }, [inputData, isAgreement]);

  const viewList = [
    <ViewPage1 key={1} />,
    <ViewPage2
      key={2}
      data={inputData}
      setData={setInputData}
      isAgreement={isAgreement}
      setIsAgreement={setIsAgreement}
      isNext={isNext}
      next={next}
      openAgreeMent={() => setIsAgreementModal(true)}
    />,
  ];

  function next() {
    if (step + 1 === viewList.length) {
      submit();
      return;
    }
    setStep((prev) => prev + 1);
  }

  function backHandler() {
    if (step > 0) {
      setStep((prev) => prev - 1);
      return false;
    }
    return true;
  }

  async function submit() {
    try {
      if (!inputData.company_name) {
        return alert("회사명을 입력해주세요.");
      } else if ((inputData.files || []).length === 0) {
        return alert("사원증 또는 재직증명서를 등록해주세요.");
      } else if (
        inputData.type === "가족" &&
        (inputData.files2 || []).length === 0
      ) {
        return alert("가족관계증명서를 등록해주세요.");
      } else if (!isAgreement) {
        return alert(
          "선택 정보 수집 및 이용 동의에 체크 해주세요. 미동의시 혜택이 제공될 수 없습니다.",
        );
      }

      const params = {
        ...inputData,
        extra_data: passData,
        files: [
          ...(inputData.files || []).map((item) => ({
            ...item,
            title: "인증서류",
          })),
          ...(inputData.files2 || []).map((item) => ({
            ...item,
            title: "가족인증서류",
          })),
        ],
      };
      const { success, message } = await post(
        "/api/user/createPromotionB2b",
        params,
      );
      if (success) {
        alert(
          "임직원 신청이 정상적으로 완료 되었습니다.관리자 확인 후 승인처리되며, 승인여부는 카카오톡으로 안내 받으실 예정입니다",
        );
        navigate({ url: ScreenId.HOME });
      } else {
        alert(message);
      }
    } catch (error) {
      console.warn("submit error", error);
    }
  }

  useEffect(() => {
    // 기 신청자 막기
    if (!!user?.auth_type) {
      alert(`이미 ${user?.auth_type} 인증 완료되었습니다.`);
      return navigate({ url: ScreenId.SIGN_IN });
    }
    if (!!user?.wait_auth_type) {
      alert(`${user?.wait_auth_type} 인증 승인 대기중입니다.`);
      return navigate({ url: ScreenId.SIGN_IN });
    }

    const handleMessage = (event: MessageEvent) => {
      // origin 확인 (보안 강화용)
      // if (event.origin !== "https://nice.checkplus.co.kr") {
      //   console.warn("Unknown message origin:", event.origin);
      //   return;
      // }

      const data = event.data;

      if (data.success) {
        // 인증 성공 처리
        setPassData(data.data);
        next();
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    setHeader((prev) => ({ ...prev, backHandler }));
  }, [step]);

  return (
    <>
      <div className={styles.container}>
        <div
          className={styles.slideContainer}
          style={{
            width: `${viewList.length * 100}%`,
            left: `-${step * 100}%`,
          }}
        >
          {viewList}
        </div>
      </div>
      <AgreementModal
        visible={isAgreementModal}
        close={() => setIsAgreementModal(false)}
      />
    </>
  );
}

function ViewPage1() {
  async function openCheckPage() {
    try {
      const { message, success, data } = await post<{ result: INiceapiMain }>(
        "/api/niceapi/checkPlusMain",
      );

      if (!success) {
        alert(message);
        return;
      }

      const { token_version_id, enc_data, integrity } = data.result || {};

      // 팝업 창 먼저 오픈 (팝업 차단 방지)
      const popup = window.open(
        "",
        "popupChk",
        "width=480,height=812,top=100,left=100,fullscreen=no,menubar=no,status=no,toolbar=no,titlebar=yes,location=no,scrollbars=no",
      );

      if (!popup) {
        alert("팝업이 차단되었습니다. 브라우저 팝업 차단을 해제해주세요.");
        return;
      }

      // 동적으로 form 생성
      const form = document.createElement("form");
      form.method = "POST";
      form.action =
        "https://nice.checkplus.co.kr/CheckPlusSafeModel/checkplus.cb";
      form.target = "popupChk";

      const inputHidden = (name: string, value: string) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        return input;
      };

      form.appendChild(inputHidden("m", "service"));
      form.appendChild(inputHidden("token_version_id", token_version_id));
      form.appendChild(inputHidden("enc_data", enc_data));
      form.appendChild(inputHidden("integrity_value", integrity));

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      console.error("openCheckPage error", error);
    }
  }

  async function openCheckPage_test() {
    try {
      // 팝업 창 먼저 오픈 (팝업 차단 방지)
      const popup = window.open(
        "",
        "popupChk",
        "width=480,height=812,top=100,left=100,fullscreen=no,menubar=no,status=no,toolbar=no,titlebar=yes,location=no,scrollbars=no",
      );

      if (!popup) {
        alert("팝업이 차단되었습니다. 브라우저 팝업 차단을 해제해주세요.");
        return;
      }

      // 동적으로 form 생성
      const form = document.createElement("form");
      form.method = "POST";
      form.action = "http://localhost:8080/api/niceapi/test";
      form.target = "popupChk";

      const inputHidden = (name: string, value: string) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        return input;
      };

      form.appendChild(inputHidden("m", "service"));

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      console.error("openCheckPage error", error);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageTitle}>
        임직원 인증을 위해{"\n"}본인 인증을 진행 해주세요!
      </div>
      <button
        className={[styles.btn, styles.active].join(" ")}
        onClick={openCheckPage}
      >
        본인 인증
      </button>
    </div>
  );
}

function ViewPage2({
  data,
  setData,
  isAgreement,
  setIsAgreement,
  isNext,
  next,
  openAgreeMent,
}: {
  data: IInputData;
  setData: (data: IInputData) => void;
  isAgreement: boolean;
  setIsAgreement: (data: boolean) => void;
  isNext: boolean;
  next: () => void;
  openAgreeMent: () => void;
}) {
  return (
    <div className={styles.page}>
      <div className={styles.row}>
        <div className={styles.rowTitle}>
          <span>회사명</span>
        </div>
        <div className={styles.rowContent}>
          <Select
            containerClassName={styles.selectBox}
            className={styles.select}
            controlClassName={styles.selectControl}
            value={data.company_name}
            singleSelect={(value) => setData({ ...data, company_name: value })}
            list={CONSTANTS.B2B_COMPANY_LIST.map((item) => ({
              value: item,
              label: item,
            }))}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.rowTitle}>
          <span>구분</span>
        </div>
        <div className={styles.rowContent}>
          <div className={styles.radioContainer}>
            {(["본인", "가족"] as TType[]).map((item, index) => {
              const isSelected = data.type === item;

              return (
                <div
                  key={index}
                  className={styles.radio}
                  onClick={() => setData({ ...data, type: item })}
                >
                  <div
                    className={[
                      styles.radioBtn,
                      !!isSelected && styles.radioSelected,
                    ].join(" ")}
                  >
                    <span></span>
                  </div>
                  <FontText>{item}</FontText>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className={styles.fileUploadTitle}>
        <span>사원증</span> 또는 <span>재직증명서</span>를 등록해주세요. (택 1)
      </div>
      <div className={styles.uploadContainer}>
        <FileUploadButton
          containerClassname={styles.uploadBtn}
          disabled={(data.files || []).length >= 1}
          children={
            <div className={styles.uploadBtnContent}>
              <Svgs.SvgPlus />
              <span>파일첨부</span>
            </div>
          }
          type="all"
          accept="image/*,.pdf"
          uploadUrl={"/api/utils/promotionUploadImage"}
          uploadFileUrl={"api/utils/b2bUploadFile"}
          fileUpload={(_files) => {
            const newFiles: IFiles[] = [
              ...(_files.files || []).map((item, index) => {
                const imgUrl = item;
                const fileName = _files.fileData[index].name;
                return {
                  url: imgUrl,
                  thumb_img_url: imgUrl,
                  origin_file_name: fileName,
                };
              }),
            ];
            setData({ ...data, files: [...(data.files || []), ...newFiles] });
          }}
        />
        {(data.files || []).map((item, index) => (
          <div className={styles.fileItem} key={index}>
            <div>
              <Svgs.SvgDocument />
            </div>
            <div>{item.origin_file_name}</div>
            <div
              onClick={() =>
                setData({
                  ...data,
                  files: (data.files || []).filter(
                    (values, _index) => index !== _index,
                  ),
                })
              }
            >
              <Svgs.SvgClose />
            </div>
          </div>
        ))}

        {data.type === "가족" && (
          <>
            <div className={styles.fileUploadTitle}>
              가족관계 확인을 위해
              <br />
              <span>주민등록등본</span> 또는 <span>가족관계증명서</span>를
              첨부해주세요.
            </div>
            <div className={styles.privateInfo}>
              <Svgs.SvgAlert stroke="#79C5BC" />
              <div>개인정보 보호를 위해 주민번호 뒷자리는 마스킹 해주세요!</div>
            </div>
            <div className={styles.uploadContainer}>
              <FileUploadButton
                containerClassname={styles.uploadBtn}
                children={
                  <div className={styles.uploadBtnContent}>
                    <Svgs.SvgPlus />
                    <span>파일첨부</span>
                  </div>
                }
                uploadUrl={"/api/utils/promotionUploadImage"}
                accept="image/*,.pdf"
                fileUpload={(_files) => {
                  const newFiles: IFiles[] = [
                    ...(_files.files || []).map((item, index) => {
                      const imgUrl = item;
                      const fileName = _files.fileData[index].name;
                      return {
                        url: imgUrl,
                        thumb_img_url: imgUrl,
                        origin_file_name: fileName,
                      };
                    }),
                  ];
                  setData({
                    ...data,
                    files2: [...(data.files2 || []), ...newFiles],
                  });
                }}
              />
              {(data.files2 || []).map((item, index) => (
                <div className={styles.fileItem} key={index}>
                  <div>
                    <Svgs.SvgDocument />
                  </div>
                  <div>{item.origin_file_name}</div>
                  <div
                    onClick={() =>
                      setData({
                        ...data,
                        files2: (data.files2 || []).filter(
                          (values, _index) => index !== _index,
                        ),
                      })
                    }
                  >
                    <Svgs.SvgClose />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className={styles.fileDesc}>
          <div>*</div>
          <div>
            jpg, png, pdf 파일만 첨부 가능하며, 최대 10MB 까지 첨부가능합니다.
          </div>
        </div>
      </div>
      <div className={styles.agree}>
        <div className={styles.agreeItem}>
          <div
            className={[styles.checkbox, isAgreement && styles.active].join(
              " ",
            )}
            onClick={() => setIsAgreement(!isAgreement)}
          >
            <SvgCheckLine fill={"white"} />
          </div>
          <div className={styles.text}>선택 정보 수집 및 이용 동의</div>
          <button className={styles.agreeBtn} onClick={openAgreeMent}>
            전문보기
          </button>
        </div>
      </div>
      <button
        className={[styles.confirmBtn, isNext && styles.active].join(" ")}
        onClick={next}
      >
        등록할게요
      </button>
      <div className={styles.bottomInfo}>
        <div className={[styles.row, styles.title].join(" ")}>
          <Svgs.SvgAlert />
          임직원 인증 안내
        </div>
        <div className={styles.row}>
          <ul>
            <li>본인인증과 일치된 회원 정보를 확인합니다.</li>
            <li>
              사원증 및 재직증명서 확인하여 관리자 승인 후 혜택을 드립니다.{" "}
            </li>
            <li>
              첨부해주신 정보는 임직원 인증 용도 외에는 사용되지 않으며 안전하게
              폐기됩니다
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function AgreementModal({
  visible,
  close,
}: {
  visible: boolean;
  close?: () => void;
}) {
  return (
    <Modal
      visible={!!visible}
      isCenter={true}
      backOpacity={70}
      className={styles.agreementModal}
      close={close}
      toWay="top"
    >
      <div className={styles.wrap} onClick={close}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.title}>
            <FontText weight="bold" size={16} lineHeight={31}>
              선택 정보 수집 및 이용 동의
            </FontText>
            <div onClick={close}>
              <SvgX color="#9F9F9F" />
            </div>
          </div>
          <div className={styles.line} />
          <div className={styles.desc}>
            메이크마이웨딩은 회원인증을 위하여 아래와 같이 개인정보를
            수집·이용하고자 합니다.
            <br />
            내용을 자세히 읽으신 후 동의 여부를 결정하여 주십시오.
            <br />
            <br />
            [필수] 개인정보 수집·이용 동의
            <br />
            수집·이용 목적: 회원 등급 혜택 <br />
            수집 항목: 이름, 휴대폰번호, 회사명, 사원증 또는 재직증명서,
            가족관계 증명서
            <br />
            보유·이용 기간: 3년
            <br />
            <br />※ 고객님께서는 개인정보 수집 및 이용에 대한 동의를 거부할
            권리가 있습니다. 단 동의하지 않으실 경우 참여에 제한이 있을 수
            있습니다.
          </div>
        </div>
      </div>
    </Modal>
  );
}
